/* STARSEED-CAPMARKETS-293 — render a FastAPI error as text a human can act on.

   The call sites all did `toast.error(error.response?.data?.detail || fallback)`.
   That works for a `HTTPException(detail="...")`, where `detail` is a string —
   but on a 422 FastAPI returns `detail` as a LIST OF OBJECTS:

     [{ "type": "value_error", "loc": ["body", "email"], "msg": "Field required" }]

   Passed to a toast, that renders as "[object Object]" (or throws in React when
   used as a child), so the one error that explains what the operator did wrong is
   the one they cannot read. That is how the phone-only rejection presented: a
   toast with no usable text. */

/** Format one FastAPI validation error entry as `field: message`. */
function formatValidationEntry(entry) {
  if (typeof entry === "string") return entry;
  if (!entry || typeof entry !== "object") return null;

  const msg = entry.msg || entry.message;
  if (!msg) return null;

  // `loc` is like ["body", "email"] — drop the "body"/"query" prefix, which
  // names the request part rather than the field the operator sees.
  const loc = Array.isArray(entry.loc)
    ? entry.loc.filter((p) => !["body", "query", "path", "header"].includes(p))
    : [];

  // Pydantic prefixes its own messages with "Value error, " — noise here.
  const cleaned = String(msg).replace(/^Value error,\s*/, "");
  return loc.length ? `${loc.join(".")}: ${cleaned}` : cleaned;
}

/**
 * Extract a human-readable message from an axios error.
 *
 * @param {unknown} error    the thrown axios error
 * @param {string}  fallback used when the response carries nothing readable
 * @returns {string}         always a string — never an object
 */
export function readApiError(error, fallback = "Something went wrong") {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const parts = detail.map(formatValidationEntry).filter(Boolean);
    if (parts.length) return parts.join("; ");
  }

  // A single validation object rather than a list.
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const one = formatValidationEntry(detail);
    if (one) return one;
  }

  // Some routes return `{ error: "..." }` instead of `{ detail: "..." }`.
  const err = error?.response?.data?.error;
  if (typeof err === "string" && err.trim()) return err;

  return fallback;
}

export default readApiError;
